import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionApi, Transaction } from '../api/TransactionApi';
import { PersonalCabinetApi } from '../api/PersonalCabinetApi';

type Company = { id: number; name: string; location?: string };

export default function TransactionHistoryPage() {
  const role = localStorage.getItem('role');
  const { t, i18n } = useTranslation();
  
  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | 'ALL' | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [salesHistory, setSalesHistory] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'sales' | 'analytics'>('all');
  
  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Analytics state
  const [analyticsDateRange, setAnalyticsDateRange] = useState<'7' | '30' | '90' | '365'>('30');

  const loadCompanies = useCallback(async () => {
    try {
      const res = await PersonalCabinetApi.getPersonalCompanies();
      const companies = res.data || [];
      
      setCompanies(companies);
      
      // Set default company selection
      const stored = localStorage.getItem('selectedCompanyId');
      if (role === 'ROLE_ADMIN') {
        const initial = stored === 'ALL' ? 'ALL' : 
          (stored && companies.some((c: Company) => c.id === Number(stored)) ? Number(stored) : 'ALL');
        setSelectedCompanyId(initial);
      } else {
        const initial = (stored && companies.some((c: Company) => c.id === Number(stored))) ? 
          Number(stored) : (companies[0]?.id ?? null);
        setSelectedCompanyId(initial);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, [role]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Skip loading if no company is selected or "ALL" is selected for non-admins
      if (!selectedCompanyId || (selectedCompanyId === 'ALL' && role !== 'ROLE_ADMIN')) {
        setTransactions([]);
        setSalesHistory([]);
        return;
      }

      // For admins with "ALL" selected, we'll need to aggregate from all companies
      if (role === 'ROLE_ADMIN' && selectedCompanyId === 'ALL') {
        // This would require a different API endpoint or multiple calls
        // For now, we'll just show empty state
        setTransactions([]);
        setSalesHistory([]);
        return;
      }

      const companyId = Number(selectedCompanyId);
      const filters: any = {};
      
      // Add date filters if provided
      if (startDate) filters.startDate = new Date(startDate).toISOString();
      if (endDate) filters.endDate = new Date(endDate).toISOString();
      
      if (activeTab === 'all') {
        // Add transaction type filter if provided
        if (filterType) filters.transactionType = filterType;
        
        const res = await TransactionApi.getAllTransactions(companyId, filters);
        setTransactions(res.data || []);
      } else if (activeTab === 'sales') {
        const res = await TransactionApi.getSalesHistory(companyId);
        setSalesHistory(res.data || []);
      } else if (activeTab === 'analytics') {
        // For analytics, we need all transactions
        const res = await TransactionApi.getAllTransactions(companyId, filters);
        setTransactions(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, endDate, filterType, role, selectedCompanyId, startDate]);

  // Load companies on mount and whenever the authenticated role changes.
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Load transactions when the selected company, tab, or server filters change.
  useEffect(() => {
    if (selectedCompanyId !== null) {
      loadTransactions();
    }
  }, [loadTransactions, selectedCompanyId]);

  // Filter transactions
  const filteredTransactions = (activeTab === 'all' ? transactions : salesHistory).filter(t => {
    const matchesType = !filterType || t.transactionType === filterType;
    const matchesProduct = !filterProduct || 
      t.product.name.toLowerCase().includes(filterProduct.toLowerCase());
    const matchesStartDate = !startDate || new Date(t.transactionDate) >= new Date(startDate);
    const matchesEndDate = !endDate || new Date(t.transactionDate) <= new Date(endDate);
    
    return matchesType && matchesProduct && matchesStartDate && matchesEndDate;
  });

  const formatCurrency = (amount: number) => t('currency.format', { amount: amount.toFixed(2) });
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Use the current language for date formatting
    const locale = i18n.language === 'ro' ? 'ro-RO' : 
                   i18n.language === 'ru' ? 'ru-RU' : 'en-US';
    return date.toLocaleString(locale);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'SALE': return '#4caf50';
      case 'STOCK_ADDITION':
      case 'STOCK_IN': return '#2196f3';
      case 'STOCK_REMOVAL':
      case 'STOCK_OUT': return '#ff9800';
      case 'STOCK_ADJUSTMENT':
      case 'INVENTORY_ACTUALIZATION': return '#7e57c2';
      case 'RETURN': return '#9c27b0';
      default: return '#757575';
    }
  };

  const getTransactionTypeDisplay = (type: string) => {
    switch (type) {
      case 'SALE': return t('transaction.sale');
      case 'STOCK_ADDITION':
      case 'STOCK_IN': return t('transaction.stockIn');
      case 'STOCK_REMOVAL':
      case 'STOCK_OUT': return t('transaction.stockOut');
      case 'STOCK_ADJUSTMENT':
      case 'INVENTORY_ACTUALIZATION': return t('transaction.inventoryActualization');
      case 'RETURN': return t('transaction.return');
      case 'TRANSFER_IN': return t('transaction.stockIn'); // Fallback
      case 'TRANSFER_OUT': return t('transaction.stockOut'); // Fallback
      case 'ADJUSTMENT': return t('transaction.stockOut'); // Fallback
      default: return type;
    }
  };

  // Analytics calculations
  const getAnalyticsData = () => {
    const now = new Date();
    const daysAgo = parseInt(analyticsDateRange);
    const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    
    const filteredTx = transactions.filter(t => new Date(t.transactionDate) >= cutoffDate);
    
    // Group by transaction type
    const byType: { [key: string]: { count: number; total: number } } = {};
    filteredTx.forEach(t => {
      if (!byType[t.transactionType]) {
        byType[t.transactionType] = { count: 0, total: 0 };
      }
      byType[t.transactionType].count += t.quantity;
      byType[t.transactionType].total += t.totalAmount || 0;
    });
    
    // Group by date for daily trend
    const byDate: { [key: string]: { sales: number; revenue: number; profit: number } } = {};
    filteredTx.forEach(t => {
      const date = new Date(t.transactionDate).toLocaleDateString();
      if (!byDate[date]) {
        byDate[date] = { sales: 0, revenue: 0, profit: 0 };
      }
      if (t.transactionType === 'SALE') {
        byDate[date].sales += t.quantity;
        byDate[date].revenue += t.totalAmount || 0;
        // Calculate profit if cost data available
        const unitCost = (t.product.acquisitionCost || 0) + (t.product.deliveryCost || 0) + 
                        (t.product.adsCost || 0) + (t.product.otherCosts || 0);
        byDate[date].profit += ((t.unitPrice || 0) - unitCost) * t.quantity;
      }
    });
    
    // Top products
    const productStats: { [key: string]: { name: string; quantity: number; revenue: number; profit: number } } = {};
    filteredTx.forEach(t => {
      if (t.transactionType === 'SALE') {
        const key = `${t.product.id}`;
        if (!productStats[key]) {
          productStats[key] = { 
            name: `${t.product.name} (${t.product.firm}-${t.product.model}-${t.product.size})`,
            quantity: 0, 
            revenue: 0,
            profit: 0
          };
        }
        productStats[key].quantity += t.quantity;
        productStats[key].revenue += t.totalAmount || 0;
        const unitCost = (t.product.acquisitionCost || 0) + (t.product.deliveryCost || 0) + 
                        (t.product.adsCost || 0) + (t.product.otherCosts || 0);
        productStats[key].profit += ((t.unitPrice || 0) - unitCost) * t.quantity;
      }
    });
    
    const topProducts = Object.values(productStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
    
    return { byType, byDate, topProducts };
  };

  return (
    <div style={{ maxWidth: 1400 }}>
      <h2 style={{ color: '#1976d2', marginBottom: 24 }}>{t('transactionHistory.title')}</h2>

      {/* Company Selector */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#1976d2' }}>
          {t('common.company')}:
        </label>
        <select
          value={selectedCompanyId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            const newValue = v === 'ALL' ? 'ALL' : Number(v);
            setSelectedCompanyId(newValue);
            localStorage.setItem('selectedCompanyId', v);
          }}
          style={{ 
            padding: '8px 12px', 
            borderRadius: 6, 
            border: '1px solid #1976d2', 
            fontSize: 14, 
            minWidth: 220 
          }}
        >
          {role === 'ROLE_ADMIN' && <option value="ALL">{t('common.allCompanies')}</option>}
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}{company.location ? ` (${company.location})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 24, borderBottom: '2px solid #e0e0e0' }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'all' ? '#1976d2' : 'transparent',
            color: activeTab === 'all' ? '#fff' : '#1976d2',
            borderRadius: '6px 6px 0 0',
            marginRight: 8,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          {t('transactionHistory.allTransactions')}
        </button>
        <button
          onClick={() => setActiveTab('sales')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'sales' ? '#1976d2' : 'transparent',
            color: activeTab === 'sales' ? '#fff' : '#1976d2',
            borderRadius: '6px 6px 0 0',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          {t('transactionHistory.salesHistory')}
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'analytics' ? '#1976d2' : 'transparent',
            color: activeTab === 'analytics' ? '#fff' : '#1976d2',
            borderRadius: '6px 6px 0 0',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          {t('transactionHistory.analytics')}
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          {/* Date Range Selector */}
          <div style={{ marginBottom: 24, padding: 16, background: '#f5f8fa', borderRadius: 8 }}>
            <label style={{ fontWeight: 600, marginRight: 12, color: '#1976d2' }}>{t('common.date')} Range:</label>
            {(['7', '30', '90', '365'] as const).map(days => (
              <button
                key={days}
                onClick={() => setAnalyticsDateRange(days)}
                style={{
                  padding: '8px 16px',
                  marginRight: 8,
                  background: analyticsDateRange === days ? '#1976d2' : '#fff',
                  color: analyticsDateRange === days ? '#fff' : '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {t(`transactionHistory.last${days}Days`)}
              </button>
            ))}
          </div>

          {(() => {
            const { byType, byDate, topProducts } = getAnalyticsData();
            const totalRevenue = Object.values(byType).reduce((sum, t) => sum + t.total, 0);
            const totalSales = byType['SALE']?.count || 0;
            const totalProfit = Object.values(byDate).reduce((sum, d) => sum + d.profit, 0);

            return (
              <>
                {/* Summary Cards */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: 16, 
                  marginBottom: 24 
                }}>
                  <div style={{ 
                    padding: 20, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    borderRadius: 12, 
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ fontSize: 14, opacity: 0.9 }}>{t('transactionHistory.totalRevenue')}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
                      {formatCurrency(totalRevenue)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    padding: 20, 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                    borderRadius: 12, 
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ fontSize: 14, opacity: 0.9 }}>{t('transactionHistory.totalSales')}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
                      {totalSales} {t('common.quantity').toLowerCase()}
                    </div>
                  </div>
                  
                  <div style={{ 
                    padding: 20, 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                    borderRadius: 12, 
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ fontSize: 14, opacity: 0.9 }}>{t('transactionHistory.totalProfit')}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
                      {formatCurrency(totalProfit)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    padding: 20, 
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
                    borderRadius: 12, 
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ fontSize: 14, opacity: 0.9 }}>{t('transactionHistory.averageMargin')}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
                      {totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : '0'}%
                    </div>
                  </div>
                </div>

                {/* Transaction Type Distribution */}
                <div style={{ marginBottom: 32, padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ color: '#1976d2', marginBottom: 20 }}>📈 {t('transactionHistory.transactionTypeDistribution')}</h3>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {Object.entries(byType).map(([type, data]) => {
                      const percentage = totalRevenue > 0 ? (data.total / totalRevenue * 100) : 0;
                      const color = getTransactionTypeColor(type);
                      
                      return (
                        <div key={type} style={{ flex: '1 1 200px', minWidth: 200 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontWeight: 600 }}>{getTransactionTypeDisplay(type)}</span>
                            <span style={{ color: '#666' }}>{percentage.toFixed(1)}%</span>
                          </div>
                          <div style={{ 
                            height: 8, 
                            background: '#e0e0e0', 
                            borderRadius: 4, 
                            overflow: 'hidden' 
                          }}>
                            <div style={{ 
                              width: `${percentage}%`, 
                              height: '100%', 
                              background: color,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
                            {data.count} units • {formatCurrency(data.total)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Daily Sales Trend */}
                <div style={{ marginBottom: 32, padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ color: '#1976d2', marginBottom: 20 }}>📅 {t('transactionHistory.dailySalesTrend')}</h3>
                  <div style={{ overflowX: 'auto' }}>
                    {Object.keys(byDate).length > 0 ? (
                      <>
                        {/* Bar Chart Visualization */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 20, minHeight: 200 }}>
                          {Object.entries(byDate).sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()).map(([date, data]) => {
                            const maxRevenue = Math.max(...Object.values(byDate).map(d => d.revenue));
                            const height = maxRevenue > 0 ? (data.revenue / maxRevenue * 150) : 0;
                            
                            return (
                              <div key={date} style={{ flex: 1, minWidth: 60, textAlign: 'center' }}>
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#1976d2' }}>
                                  {formatCurrency(data.revenue)}
                                </div>
                                <div style={{ 
                                  height: Math.max(height, 10), 
                                  background: 'linear-gradient(to top, #1976d2, #42a5f5)',
                                  borderRadius: '4px 4px 0 0',
                                  position: 'relative'
                                }}>
                                  {data.profit > 0 && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: `${(data.profit / data.revenue * 100)}%`,
                                      background: 'rgba(76, 175, 80, 0.5)',
                                      borderRadius: '4px 4px 0 0'
                                    }} />
                                  )}
                                </div>
                                <div style={{ fontSize: 11, color: '#666', marginTop: 8, transform: 'rotate(-45deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                                  {date.split('/')[0]}/{date.split('/')[1]}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #1976d2' }}>
                              <th style={{ padding: 12, textAlign: 'left' }}>{t('common.date')}</th>
                              <th style={{ padding: 12, textAlign: 'center' }}>{t('transactionHistory.quantitySold')}</th>
                              <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.revenue')}</th>
                              <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.profit')}</th>
                              <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.margin')} %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(byDate).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()).map(([date, data]) => (
                              <tr key={date} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td style={{ padding: 12 }}>{date}</td>
                                <td style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>{data.sales}</td>
                                <td style={{ padding: 12, textAlign: 'right' }}>{formatCurrency(data.revenue)}</td>
                                <td style={{ padding: 12, textAlign: 'right', color: data.profit >= 0 ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                                  {formatCurrency(data.profit)}
                                </td>
                                <td style={{ padding: 12, textAlign: 'right' }}>
                                  {data.revenue > 0 ? ((data.profit / data.revenue) * 100).toFixed(1) : '0'}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                        {t('transactionHistory.noDataForPeriod')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Products */}
                <div style={{ padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ color: '#1976d2', marginBottom: 20 }}>🏆 {t('transactionHistory.topProducts')}</h3>
                  {topProducts.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #1976d2' }}>
                          <th style={{ padding: 12, textAlign: 'left' }}>Rank</th>
                          <th style={{ padding: 12, textAlign: 'left' }}>{t('common.product')}</th>
                          <th style={{ padding: 12, textAlign: 'center' }}>{t('transactionHistory.totalSold')}</th>
                          <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.revenue')}</th>
                          <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.profit')}</th>
                          <th style={{ padding: 12, textAlign: 'right' }}>{t('transactionHistory.margin')} %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProducts.map((product, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: 12, fontWeight: 700, fontSize: 18, color: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : '#999' }}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`}
                            </td>
                            <td style={{ padding: 12, fontWeight: 600 }}>{product.name}</td>
                            <td style={{ padding: 12, textAlign: 'center' }}>{product.quantity}</td>
                            <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(product.revenue)}</td>
                            <td style={{ padding: 12, textAlign: 'right', color: product.profit >= 0 ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                              {formatCurrency(product.profit)}
                            </td>
                            <td style={{ padding: 12, textAlign: 'right' }}>
                              {product.revenue > 0 ? ((product.profit / product.revenue) * 100).toFixed(1) : '0'}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      {t('transactionHistory.noDataForPeriod')}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Filters - Only show for 'all' and 'sales' tabs */}
      {activeTab !== 'analytics' && (
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          marginBottom: 24, 
          flexWrap: 'wrap',
          padding: 16,
          background: '#f5f8fa',
          borderRadius: 8
        }}>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
        >
          <option value="">{t('transactionHistory.allTypes')}</option>
          <option value="SALE">{t('transactionHistory.sale')}</option>
          <option value="STOCK_ADDITION">{t('transactionHistory.stockIn')}</option>
          <option value="STOCK_REMOVAL">{t('transactionHistory.stockOut')}</option>
          <option value="STOCK_ADJUSTMENT">{t('transactionHistory.inventoryActualization')}</option>
          <option value="INVENTORY_ACTUALIZATION">{t('transactionHistory.inventoryActualization')}</option>
          <option value="RETURN">{t('transactionHistory.return')}</option>
        </select>

        <input
          type="text"
          placeholder={t('transactionHistory.filterByProduct')}
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14, minWidth: 200 }}
        />

        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
        />

        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}
        />

        <button
          onClick={() => {
            setFilterType('');
            setFilterProduct('');
            setStartDate('');
            setEndDate('');
          }}
          style={{
            padding: '8px 16px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          {t('common.resetFilters')}
        </button>
      </div>
      )}

      {/* Loading */}
      {loading && activeTab !== 'analytics' && (
        <div style={{ textAlign: 'center', padding: 40, color: '#1976d2' }}>
          {t('common.loading')}
        </div>
      )}

      {/* Transactions Table */}
      {!loading && activeTab !== 'analytics' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'separate', 
            borderSpacing: 0, 
            background: '#f5f8fa', 
            borderRadius: 8 
          }}>
            <thead>
              <tr style={{ background: '#1976d2', color: '#fff' }}>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.date')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.type')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.product')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.company')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.deposit')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'center' }}>{t('common.quantity')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'center' }}>{t('common.unitPrice')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'center' }}>{t('common.total')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.user')}</th>
                <th style={{ padding: '12px 10px', textAlign: 'left' }}>{t('common.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#000' }}>
                    {t('transactionHistory.noTransactionsFound')}
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction, idx) => (
                  <tr key={transaction.id} style={{ 
                    background: idx % 2 === 0 ? '#fff' : '#f0f4fa', 
                    borderBottom: '2px solid #e0e0e0' 
                  }}>
                    <td style={{ padding: '12px 10px' }}>{formatDate(transaction.transactionDate)}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        background: getTransactionTypeColor(transaction.transactionType),
                        color: 'white',
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {getTransactionTypeDisplay(transaction.transactionType)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 10px', fontWeight: 600 }}>
                      {transaction.product.name}
                      {transaction.product.firm && ` - ${transaction.product.firm}`}
                      {transaction.product.model && ` - ${transaction.product.model}`}
                      {transaction.product.size && ` (${transaction.product.size})`}
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      {transaction.company ? (
                        <div>
                          <strong>{transaction.company.name}</strong>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                          {t('common.notSpecified')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      {transaction.deposit ? (
                        <div>
                          <strong>{transaction.deposit.name}</strong>
                          {transaction.deposit.location && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              {transaction.deposit.location}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                          {t('common.notSpecified')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                      {(() => {
                        const type = transaction.transactionType;
                        const raw = transaction.quantity ?? 0;

                        if (type === 'SALE' || type === 'STOCK_REMOVAL' || type === 'STOCK_OUT') {
                          return `-${Math.abs(raw)}`;
                        }

                        if (type === 'STOCK_ADJUSTMENT' || type === 'INVENTORY_ACTUALIZATION') {
                          return raw >= 0 ? `+${raw}` : `${raw}`;
                        }

                        return `+${Math.abs(raw)}`;
                      })()}
                    </td>
                    <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                      {transaction.unitPrice ? formatCurrency(transaction.unitPrice) : '-'}
                    </td>
                    <td style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600 }}>
                      {transaction.totalAmount ? formatCurrency(transaction.totalAmount) : '-'}
                    </td>
                    <td style={{ padding: '12px 10px' }}>{transaction.user.username}</td>
                    <td style={{ padding: '12px 10px', maxWidth: 200, wordBreak: 'break-word' }}>
                      {transaction.notes || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      {filteredTransactions.length > 0 && activeTab !== 'analytics' && (
        <div style={{ 
          marginTop: 24, 
          padding: 16, 
          background: '#e3f2fd', 
          borderRadius: 8,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <strong>{t('transactionHistory.totalTransactions')}: </strong>
            {filteredTransactions.length}
          </div>
          {activeTab === 'sales' && (
            <div>
              <strong>{t('transactionHistory.totalSales')}: </strong>
              {formatCurrency(
                filteredTransactions
                  .filter(t => t.transactionType === 'SALE')
                  .reduce((sum, t) => sum + (t.totalAmount || 0), 0)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
